import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useSearchParams, useLocation } from 'react-router-dom';
import api from '../services/authService';
import { Company, Pagination as PaginationType } from '../types';
import { colors } from '../styles/colors';
import CompanyForm from './CompanyForm';
import Pagination from './Pagination';
import ConfirmModal from './ConfirmModal';
import CompanyOpportunities from './CompanyOpportunities';
import CompanySubscription from './CompanySubscription';
import CompanyRecruiters from './CompanyRecruiters';
import CompanySubscriptionsModal from './CompanySubscriptionsModal';
import CompanySubscriptionCreateModal from './CompanySubscriptionCreateModal';
import CompanyCredits from './CompanyCredits';
import CompanyTrialModal from './CompanyTrialModal';
import { formatCNPJ } from '../utils/formatters';

const Companies: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const skipNextFetch = useRef(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('name');
  const [scope, setScope] = useState('all');
  const [subscriberFilter, setSubscriberFilter] = useState('all');
  const isInitialMount = useRef(true);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (searchType === 'cnpj') {
      const numbers = value.replace(/\D/g, '');
      const formatted = numbers.length <= 14 
        ? numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
        : numbers.slice(0, 14).replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      setSearch(formatted);
    } else {
      setSearch(value);
    }
  };
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | undefined>();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [companyToToggle, setCompanyToToggle] = useState<Company | null>(null);
  const [hoveredLink, setHoveredLink] = useState<number | null>(null);
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [showOpportunities, setShowOpportunities] = useState(false);
  const [opportunitiesCompany, setOpportunitiesCompany] = useState<Company | null>(null);
  const [showSubscription, setShowSubscription] = useState(false);
  const [subscriptionCompany, setSubscriptionCompany] = useState<Company | null>(null);
  const [showRecruiters, setShowRecruiters] = useState(false);
  const [recruitersCompany, setRecruitersCompany] = useState<Company | null>(null);
  const [showSubscriptionsModal, setShowSubscriptionsModal] = useState(false);
  const [subscriptionsModalCompany, setSubscriptionsModalCompany] = useState<Company | null>(null);
  const [showCreateSubscription, setShowCreateSubscription] = useState(false);
  const [createSubscriptionCompany, setCreateSubscriptionCompany] = useState<Company | null>(null);
  const [showCredits, setShowCredits] = useState(false);
  const [creditsCompany, setCreditsCompany] = useState<Company | null>(null);
  const [showTrial, setShowTrial] = useState(false);
  const [trialCompany, setTrialCompany] = useState<Company | null>(null);

  useEffect(() => {
    const companyId = searchParams.get('companyId');
    const action = searchParams.get('action');
    if (companyId) {
      skipNextFetch.current = true;
      setSearchParams({});
      setSearchType('id');
      setSearch(companyId);
      setSearchTerm(companyId);
      setPagination(prev => ({ ...prev, page: 1 }));
      
      // Fetch direto
      const fetchDirect = async () => {
        try {
          setLoading(true);
          const params = new URLSearchParams({
            page: '1',
            limit: pagination.limit.toString(),
            search: companyId,
            searchType: 'id',
          });
          const response = await api.get(`/companies?${params}`);
          const fetchedCompanies = response.data.companies || [];
          setCompanies(fetchedCompanies);
          setPagination(response.data.pagination);
          if (action === 'edit' && fetchedCompanies[0]) {
            setEditingCompany(fetchedCompanies[0]);
            setShowForm(true);
          }
        } catch (error: any) {
          if (error.name !== 'CanceledError') {
            toast.error('Erro ao carregar empresas');
          }
        } finally {
          setLoading(false);
        }
      };
      fetchDirect();
    }
  }, [location.search]);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!searchParams.get('companyId')) {
        fetchCompanies();
      }
      return;
    }
    fetchCompanies();
  }, [pagination.page, searchTerm, scope, subscriberFilter]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(scope !== 'all' && { status: scope === 'active' ? '1' : '0' }),
        ...(subscriberFilter !== 'all' && { subscriber: subscriberFilter === 'subscriber' ? '1' : '0' }),
        ...(searchTerm && { search: searchTerm, searchType }),
      });

      const response = await api.get(`/companies?${params}`);
      setCompanies(response.data.companies);
      setPagination(response.data.pagination)
      
    } catch (error: any) {
      if (error.name !== 'CanceledError') {
        toast.error('Erro ao carregar empresas');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(search);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchTypeChange = (newType: string) => {
    setSearchType(newType);
    setSearch('');
    setSearchTerm('');
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleCreate = () => {
    setEditingCompany(undefined);
    setShowForm(true);
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setShowForm(true);
  };

  const handleToggleStatus = (company: Company) => {
    setCompanyToToggle(company);
    setShowConfirmModal(true);
  };

  const handleView = (company: Company) => {
    const detailsUrl = new URL(`/companies/${company.id}/details`, window.location.origin).toString();
    window.open(detailsUrl, '_blank', 'noopener,noreferrer');
  };

  const handleViewOpportunities = (company: Company) => {
    setOpportunitiesCompany(company);
    setShowOpportunities(true);
  };

  const handleCloseOpportunities = () => {
    setShowOpportunities(false);
    setOpportunitiesCompany(null);
  };

  const handleViewSubscription = (company: Company) => {
    setSubscriptionCompany(company);
    setShowSubscription(true);
  };

  const handleCloseSubscription = () => {
    setShowSubscription(false);
    setSubscriptionCompany(null);
  };

  const handleViewRecruiters = (company: Company) => {
    setRecruitersCompany(company);
    setShowRecruiters(true);
  };

  const handleCloseRecruiters = () => {
    setShowRecruiters(false);
    setRecruitersCompany(null);
  };

  const handleViewSubscriptionsModal = (company: Company) => {
    setSubscriptionsModalCompany(company);
    setShowSubscriptionsModal(true);
  };

  const handleCloseSubscriptionsModal = () => {
    setShowSubscriptionsModal(false);
    setSubscriptionsModalCompany(null);
  };

  const handleCreateSubscription = (company: Company) => {
    setCreateSubscriptionCompany(company);
    setShowCreateSubscription(true);
  };

  const handleCloseCreateSubscription = () => {
    setShowCreateSubscription(false);
    setCreateSubscriptionCompany(null);
  };

  const handleViewCredits = (company: Company) => {
    setCreditsCompany(company);
    setShowCredits(true);
  };

  const handleCloseCredits = () => {
    setShowCredits(false);
    setCreditsCompany(null);
  };

  const handleCreateTrial = (company: Company) => {
    setTrialCompany(company);
    setShowTrial(true);
  };

  const handleCloseTrial = () => {
    setShowTrial(false);
    setTrialCompany(null);
  };

  const toggleActionMenu = (companyId: number) => {
    setOpenActionMenu(openActionMenu === companyId ? null : companyId);
  };

  const confirmToggleStatus = async () => {
    if (companyToToggle) {
      try {
        const newStatus = companyToToggle.status === 1 ? 0 : 1;
        await api.put(`/companies/${companyToToggle.id}`, { status: newStatus });
        toast.success(`Empresa ${newStatus === 1 ? 'ativada' : 'inativada'} com sucesso!`);
        fetchCompanies();
      } catch (error: any) {
        toast.error('Erro ao alterar status da empresa');
      }
    }
    setShowConfirmModal(false);
    setCompanyToToggle(null);
  };

  const cancelToggleStatus = () => {
    setShowConfirmModal(false);
    setCompanyToToggle(null);
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditingCompany(undefined);
    fetchCompanies();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCompany(undefined);
  };

  return (
    <div style={styles.container} onClick={() => setOpenActionMenu(null)}>
      <div style={styles.header}>
        <h1 style={styles.title}>Empresas</h1>
        <button onClick={handleCreate} style={styles.createBtn}>
          Nova Empresa
        </button>
      </div>

      <div style={styles.scopes}>
        <button 
          onClick={() => { setScope('all'); setPagination(prev => ({ ...prev, page: 1 })); }} 
          style={{...styles.scopeBtn, ...(scope === 'all' ? styles.scopeBtnActive : {})}}
        >
          Todos
        </button>
        <button 
          onClick={() => { setScope('active'); setPagination(prev => ({ ...prev, page: 1 })); }} 
          style={{...styles.scopeBtn, ...(scope === 'active' ? styles.scopeBtnActive : {})}}
        >
          Ativo
        </button>
        <button 
          onClick={() => { setScope('inactive'); setPagination(prev => ({ ...prev, page: 1 })); }} 
          style={{...styles.scopeBtn, ...(scope === 'inactive' ? styles.scopeBtnActive : {})}}
        >
          Inativo
        </button>
        <div style={styles.divider}></div>
        <button 
          onClick={() => { setSubscriberFilter('all'); setPagination(prev => ({ ...prev, page: 1 })); }} 
          style={{...styles.scopeBtn, ...(subscriberFilter === 'all' ? styles.scopeBtnActive : {})}}
        >
          Todos
        </button>
        <button 
          onClick={() => { setSubscriberFilter('subscriber'); setPagination(prev => ({ ...prev, page: 1 })); }} 
          style={{...styles.scopeBtn, ...(subscriberFilter === 'subscriber' ? styles.scopeBtnActive : {})}}
        >
          Assinante
        </button>
        <button 
          onClick={() => { setSubscriberFilter('non-subscriber'); setPagination(prev => ({ ...prev, page: 1 })); }} 
          style={{...styles.scopeBtn, ...(subscriberFilter === 'non-subscriber' ? styles.scopeBtnActive : {})}}
        >
          Não Assinante
        </button>
      </div>
      
      <form onSubmit={handleSearch} style={styles.filters}>
        <select
          value={searchType}
          onChange={(e) => handleSearchTypeChange(e.target.value)}
          style={styles.select}
        >
          <option value="name">Nome</option>
          <option value="cnpj">CNPJ</option>
          <option value="id">ID</option>
        </select>
        <input
          type="text"
          placeholder={`Buscar por ${searchType === 'name' ? 'nome' : searchType === 'cnpj' ? 'CNPJ' : 'ID'}...`}
          value={search}
          onChange={handleSearchChange}
          style={styles.input}
          maxLength={searchType === 'cnpj' ? 18 : undefined}
        />
        <button type="submit" style={styles.searchBtn}>
          Buscar
        </button>
      </form>

      {loading ? (
        <div style={styles.loading}>Carregando...</div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Nome</th>
                  <th style={styles.th}>Cnpj</th>
                  <th style={styles.th}>Ativo</th>
                  <th style={styles.th}>Assinante</th>
                  <th style={styles.th}>Criado em</th>
                  <th style={styles.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id} style={styles.tableRow}>
                    <td style={styles.td}>{company.id}</td>
                    <td style={styles.companyName}>
                      {company.url ? (
                        <a 
                          style={{
                            ...styles.url,
                            textDecoration: hoveredLink === company.id ? 'underline' : 'none'
                          }}
                          href={company.url} 
                          target="_blank" 
                          rel="noreferrer"
                          onMouseEnter={() => setHoveredLink(company.id)}
                          onMouseLeave={() => setHoveredLink(null)}
                        >
                          {company.name}
                        </a>
                      ) : company.name}
                    </td>
                    <td style={styles.td}>{formatCNPJ(company.cnpj)}</td>
                    <td style={styles.td}>
                      {company.status === 1 ? (
                        <span style={styles.activeBadge}>SIM</span>
                      ) : (
                        <span style={styles.inactiveBadge}>NÃO</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {company.subscriber ? (
                        <span style={styles.activeBadge}>SIM</span>
                      ) : (
                        <span style={styles.inactiveBadge}>NÃO</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {new Date(company.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td style={styles.tdActions}>
                      <div style={styles.actionMenuContainer}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(company.id);
                          }}
                          style={styles.actionMenuBtn}
                          title="Ações"
                        >
                          ⋮
                        </button>
                        {openActionMenu === company.id && (
                          <div style={styles.actionMenu}>
                            <button
                              onClick={() => {
                                handleView(company);
                                setOpenActionMenu(null);
                              }}
                              style={styles.actionMenuItem}
                            >
                              Ver Detalhes
                            </button>
                            <button
                              onClick={() => {
                                handleViewOpportunities(company);
                                setOpenActionMenu(null);
                              }}
                              style={styles.actionMenuItem}
                            >
                              Oportunidades
                            </button>
                            <button
                              onClick={() => {
                                handleViewSubscriptionsModal(company);
                                setOpenActionMenu(null);
                              }}
                              style={styles.actionMenuItem}
                            >
                              Assinaturas
                            </button>
                            <button
                              onClick={() => {
                                handleViewSubscription(company);
                                setOpenActionMenu(null);
                              }}
                              style={styles.actionMenuItem}
                            >
                              Plano Atual
                            </button>
                            <button
                              onClick={() => {
                                handleViewRecruiters(company);
                                setOpenActionMenu(null);
                              }}
                              style={styles.actionMenuItem}
                            >
                              Recrutadores
                            </button>
                            <button
                              onClick={() => {
                                handleViewCredits(company);
                                setOpenActionMenu(null);
                              }}
                              style={styles.actionMenuItem}
                            >
                              Créditos
                            </button>
                            <button
                              onClick={() => {
                                handleEdit(company);
                                setOpenActionMenu(null);
                              }}
                              style={styles.actionMenuItem}
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => {
                                handleToggleStatus(company);
                                setOpenActionMenu(null);
                              }}
                              style={{...styles.actionMenuItem, color: company.status === 1 ? colors.error : colors.success}}
                            >
                              {company.status === 1 ? 'Inativar' : 'Ativar'}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={styles.paginationContainer}>
            <div style={styles.paginationInfo}>
              Página {pagination.page} de {pagination.totalPages} ({pagination.total} total)
            </div>
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
      
      {showForm && (
        <CompanyForm
          company={editingCompany}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}
      
      {showOpportunities && opportunitiesCompany && (
        <CompanyOpportunities
          companyId={opportunitiesCompany.id}
          companyName={opportunitiesCompany.name}
          onClose={handleCloseOpportunities}
        />
      )}
      
      {showSubscription && subscriptionCompany && (
        <CompanySubscription
          companyId={subscriptionCompany.id}
          companyName={subscriptionCompany.name}
          onClose={handleCloseSubscription}
        />
      )}
      
      {showRecruiters && recruitersCompany && (
        <CompanyRecruiters
          companyId={recruitersCompany.id}
          companyName={recruitersCompany.name}
          onClose={handleCloseRecruiters}
        />
      )}
      
      {showSubscriptionsModal && subscriptionsModalCompany && (
        <CompanySubscriptionsModal
          companyId={subscriptionsModalCompany.id}
          companyName={subscriptionsModalCompany.name}
          onClose={handleCloseSubscriptionsModal}
          onCreateNew={() => {
            handleCreateSubscription(subscriptionsModalCompany);
            handleCloseSubscriptionsModal();
          }}
          onCreateTrial={() => {
            handleCreateTrial(subscriptionsModalCompany);
            handleCloseSubscriptionsModal();
          }}
        />
      )}
      
      {showCreateSubscription && createSubscriptionCompany && (
        <CompanySubscriptionCreateModal
          companyId={createSubscriptionCompany.id}
          companyName={createSubscriptionCompany.name}
          onClose={handleCloseCreateSubscription}
          onSave={() => {
            handleCloseCreateSubscription();
            fetchCompanies();
          }}
        />
      )}
      
      {showCredits && creditsCompany && (
        <CompanyCredits
          companyId={creditsCompany.id}
          companyName={creditsCompany.name}
          onClose={handleCloseCredits}
        />
      )}
      
      {showTrial && trialCompany && (
        <CompanyTrialModal
          companyId={trialCompany.id}
          companyName={trialCompany.name}
          onClose={handleCloseTrial}
          onSave={() => {
            handleCloseTrial();
            fetchCompanies();
          }}
        />
      )}
      
      <ConfirmModal
        isOpen={showConfirmModal}
        title={companyToToggle?.status === 1 ? "Confirmar Inativação" : "Confirmar Ativação"}
        message={`Tem certeza que deseja ${companyToToggle?.status === 1 ? 'inativar' : 'ativar'} a empresa ${companyToToggle?.name}?`}
        onConfirm={confirmToggleStatus}
        onCancel={cancelToggleStatus}
        confirmText={companyToToggle?.status === 1 ? "Inativar" : "Ativar"}
        cancelText="Cancelar"
      />
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
  },
  title: {
    fontSize: '2rem',
    color: colors.primary,
    marginBottom: '2rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  select: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '120px',
    backgroundColor: colors.white,
  },
  input: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '300px',
  },
  searchBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  tableContainer: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    overflow: 'visible' as const,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    overflow: 'visible' as const,
  },
  tableHeader: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  th: {
    padding: '1rem',
    textAlign: 'left' as const,
    fontWeight: '600',
  },
  tableRow: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '1rem',
    color: colors.text,
  },
  companyName: {
    maxWidth: "400px",
    padding: '1rem',
    color: colors.text,
  },
  url: {
    color: colors.primary,
    textDecoration: 'none',
  },
  paginationContainer: {
    marginTop: '2rem',
  },
  paginationInfo: {
    textAlign: 'center' as const,
    color: colors.textLight,
    marginBottom: '1rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  createBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  activeBadge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  inactiveBadge: {
    backgroundColor: colors.error,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  scopes: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  scopeBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  scopeBtnActive: {
    backgroundColor: colors.primary,
    color: colors.white,
    borderColor: colors.primary,
  },
  divider: {
    width: '1px',
    backgroundColor: colors.border,
    margin: '0 0.5rem',
  },
  iconBtn: {
    padding: '0.5rem',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.25rem',
    fontSize: '1.2rem',
    transition: 'background-color 0.2s',
    color: colors.primary,
  },
  tdActions: {
    padding: '1rem',
    color: colors.text,
    minWidth: '80px',
    position: 'relative' as const,
  },
  actionMenuContainer: {
    position: 'relative' as const,
  },
  actionMenuBtn: {
    padding: '8px 12px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  actionMenu: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1001,
    minWidth: '120px',
  },
  actionMenuItem: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
  
};

export default Companies;
